import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Tooltip,
  Textarea,
} from "@heroui/react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import {
  findActivePrice,
  findBasePrice,
  formatRegistrationMoment,
  sortPriceHistory,
} from "shared/utils/priceHistory";
import { useSession } from "@clerk/clerk-react";
import { RentalContract, ContractProduct } from "shared/types/rental-contracts";
import { applyMask } from "shared/utils/masks";
import {
  Plus,
  Trash,
  Pencil,
  Calendar,
  CurrencyEur,
  ClockCounterClockwise,
  CheckCircle,
  Clock,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { AttributeExistingContractBadge } from "shared/components/rental/components/AttributeExistingContractBadge";

interface ProductTemplate {
  id: number;
  name: string;
  ledger: string;
  indexable: boolean;
  odooProductVariantId: number;
  odooTemplateName: string;
  archived: boolean;
}

interface BuildingAttribute {
  id: number;
  name: string;
  description: string;
  template: {
    id: number;
    category: string;
    ledger: string;
    productTemplates: Array<{
      id: number;
      name: string;
      company: {
        id: number;
        name: string;
      };
    }>;
  };
  /** Non-closed contracts this attribute is still part of (shown as a phase badge in the grid). */
  contractProducts?: Array<{
    id?: number;
    contract?: {
      id?: number;
      phase?: string;
      unit?: { id?: string } | null;
      contractContacts?: Array<{ contactType?: string; resource?: { name?: string } | null }> | null;
    };
  }> | null;
}

interface EditableProduct extends ContractProduct {
  // No longer need editing properties since we use a separate modal for price changes
}

interface EditBasePriceModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: ContractProduct | null;
  onSuccess: () => void;
  onProductUpdate?: (updatedProduct: ContractProduct) => void;
}

export const EditBasePriceModal: React.FC<EditBasePriceModalProps> = ({
  isOpen,
  onClose,
  product,
  onSuccess,
  onProductUpdate,
}) => {
  const { session } = useSession();
  const [basePrice, setBasePrice] = useState("");
  const [saving, setSaving] = useState(false);

  const formatDecimalOutput = (value: number): string => {
    return value.toString().replace(".", ",");
  };

  const parseDecimalInput = (value: string): number => {
    if (!value || value.trim() === "") return 0;
    const normalizedValue = value.replace(",", ".");
    const parsed = parseFloat(normalizedValue);
    return isNaN(parsed) ? 0 : parsed;
  };

  const getBasePrice = (
    product: ContractProduct,
  ): { amount: number; priceId: number | null } => {
    const base = findBasePrice(product.price);
    return { amount: base?.amount ?? 0, priceId: base?.id ?? null };
  };

  useEffect(() => {
    if (isOpen && product) {
      const { amount } = getBasePrice(product);
      setBasePrice(formatDecimalOutput(amount));
    }
  }, [isOpen, product]);

  const handleSave = async () => {
    if (!product) return;

    if (!basePrice.trim()) {
      toast.error("Basisprijs is verplicht");
      return;
    }

    const newAmount = parseDecimalInput(basePrice);
    if (newAmount <= 0) {
      toast.error("Basisprijs moet groter zijn dan 0");
      return;
    }

    const { priceId } = getBasePrice(product);
    if (!priceId) {
      toast.error("Geen basishuurprijs gevonden voor dit product");
      return;
    }

    setSaving(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/prices/${priceId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ amount: newAmount }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to update base price");
      }

      const updatedProduct = await response.json();

      if (onProductUpdate && updatedProduct) {
        onProductUpdate(updatedProduct);
      }

      toast.success("Basisprijs succesvol bijgewerkt en naar Odoo gepusht");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error updating base price:", error);
      toast.error("Fout bij bijwerken van basisprijs");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setBasePrice("");
    onClose();
  };

  if (!product) return null;

  const { amount: currentBasePrice } = getBasePrice(product);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleClose}
      size="lg"
      className="text-neutral-primary font-normal"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <CurrencyEur
                  weight="duotone"
                  size={24}
                  className="text-brick-primary"
                />
                <h3 className="text-lg font-medium">Basisprijs bewerken</h3>
              </div>
              <p className="text-neutral-secondary text-sm">
                Product: {product.name}
              </p>
            </ModalHeader>
            <ModalBody>
              <div className="space-y-4">
                <div className="border-neutral-primary/10 bg-neutral-primary/5 rounded-lg border p-4">
                  <h4 className="mb-2 font-medium">Huidige basisprijs</h4>
                  <p className="text-moss-primary text-2xl font-bold">
                    {applyMask(currentBasePrice, "valuta_specific")}
                  </p>
                  <p className="text-neutral-secondary mt-2 text-xs">
                    Dit is de initiële huurprijs zoals vastgelegd in het
                    contract
                  </p>
                </div>

                <Input
                  label="Nieuwe basisprijs"
                  value={basePrice}
                  onChange={(e) => setBasePrice(e.target.value)}
                  placeholder="0,00"
                  isRequired
                  startContent={
                    <span className="text-neutral-secondary">€</span>
                  }
                  description="Deze wijziging wordt naar Odoo gepusht als base_price"
                />

                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-start gap-2">
                    <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500">
                      <span className="text-xs font-bold text-white">!</span>
                    </div>
                    <div className="text-sm">
                      <p className="mb-1 font-medium text-amber-800">Let op</p>
                      <p className="text-amber-700">
                        Deze wijziging bewerkt de bestaande basishuurprijs en
                        pusht deze direct naar Odoo als
                        <strong>base_price</strong>. Gebruik dit alleen als de
                        basishuurprijs is ingevuld. De logica zoekt de oudste
                        prijs in de database en wijzigt deze waarde naar de
                        hierboven ingevulde waarde. Zorg dat je dus zeker weet
                        wat je doet!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onClick={onClose}>
                Annuleren
              </Button>
              <Button color="primary" onClick={handleSave} isLoading={saving}>
                Basisprijs opslaan
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

interface PriceChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: ContractProduct | null;
  onSuccess: () => void;
  onProductUpdate?: (updatedProduct: ContractProduct) => void; // New callback for instant updates
}

export const PriceChangeModal: React.FC<PriceChangeModalProps> = ({
  isOpen,
  onClose,
  product,
  onSuccess,
  onProductUpdate,
}) => {
  const { session } = useSession();
  const [newPrice, setNewPrice] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [priceChangeReason, setPriceChangeReason] = useState("");
  const [saving, setSaving] = useState(false);

  /**
   * De prijs die vandaag geldt. Gebruikt de gedeelde regel — de laatst
   * geregistreerde van de prijzen waarvan de ingangsdatum bereikt is — zodat deze
   * modal hetzelfde bedrag toont als het Verhuur-overzicht, de indexatiemail en
   * Odoo. De vorige versie viel bij een gelijke ingangs- en registratiedatum terug
   * op "hoogste prijs wint", waardoor een latere correctie werd genegeerd.
   */
  const getCurrentPrice = (product: ContractProduct): number =>
    findActivePrice(product.price)?.amount ?? 0;

  const formatDecimalOutput = (value: number): string => {
    return value.toString().replace(".", ",");
  };

  const parseDecimalInput = (value: string): number => {
    if (!value || value.trim() === "") return 0;

    const normalizedValue = value.replace(",", ".");
    const parsed = parseFloat(normalizedValue);

    return isNaN(parsed) ? 0 : parsed;
  };

  useEffect(() => {
    if (isOpen && product) {
      const currentPrice = getCurrentPrice(product);
      setNewPrice(formatDecimalOutput(currentPrice));

      // Don't set a default date - let user choose
      setEffectiveDate("");
      setPriceChangeReason("");
    }
  }, [isOpen, product]);

  const handleSave = async () => {
    if (!product) return;

    if (!newPrice.trim()) {
      toast.error("Nieuwe prijs is verplicht");
      return;
    }

    if (!effectiveDate.trim()) {
      toast.error("Ingangsdatum is verplicht");
      return;
    }

    if (!priceChangeReason.trim()) {
      toast.error("Reden voor prijswijziging is verplicht");
      return;
    }

    setSaving(true);
    try {
      const priceData = {
        newPrice: parseDecimalInput(newPrice),
        effectiveDate: effectiveDate,
        priceChangeReason: priceChangeReason,
      };

      const response = await fetchWithAuth(
        `${API_URL}/rental/products/${product.id}/price-change`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(priceData),
        },
        session,
      );

      const updatedProduct = await response.json();

      // Update the local product state in the price history modal if callback is provided
      if (onProductUpdate && updatedProduct) {
        onProductUpdate(updatedProduct);
      }

      toast.success("Prijswijziging succesvol opgeslagen");
      onSuccess(); // This will refresh the parent modal
      onClose();
    } catch (error) {
      console.error("Error updating price:", error);
      toast.error("Fout bij opslaan van prijswijziging");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setNewPrice("");
    setEffectiveDate("");
    setPriceChangeReason("");
    onClose();
  };

  if (!product) return null;

  const currentPrice = getCurrentPrice(product);

  // Check if the effective date is in the future
  const isEffectiveDateFuture = () => {
    if (!effectiveDate) return false;
    const selectedDate = new Date(effectiveDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    return selectedDate > today;
  };

  // Check if the effective date is today or in the past
  const isEffectiveDateImmediate = () => {
    if (!effectiveDate) return false;
    const selectedDate = new Date(effectiveDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    return selectedDate <= today;
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleClose}
      size="2xl"
      className="text-neutral-primary font-normal"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <CurrencyEur
                  weight="duotone"
                  size={24}
                  className="text-brick-primary"
                />
                <h3 className="text-lg font-medium">Prijs wijzigen</h3>
              </div>
              <p className="text-neutral-secondary text-sm">
                Product: {product.name}
              </p>
            </ModalHeader>
            <ModalBody>
              <div className="space-y-4">
                <div className="border-neutral-primary/10 bg-neutral-primary/5 rounded-lg border p-4">
                  <h4 className="mb-2 font-medium">Huidige prijs</h4>
                  <p className="text-moss-primary text-2xl font-bold">
                    {applyMask(currentPrice, "valuta_specific")}
                  </p>
                </div>

                <Input
                  label="Nieuwe prijs"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  placeholder="0,00"
                  isRequired
                  startContent={
                    <span className="text-neutral-secondary">€</span>
                  }
                />

                <Input
                  label="Ingangsdatum"
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  isRequired
                />

                {/* Warning messages based on effective date */}
                {effectiveDate && isEffectiveDateFuture() && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <div className="flex items-start gap-2">
                      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500">
                        <span className="text-xs font-bold text-white">!</span>
                      </div>
                      <div className="text-sm">
                        <p className="mb-1 font-medium text-amber-800">
                          Toekomstige prijswijziging
                        </p>
                        <p className="text-amber-700">
                          Deze prijswijziging wordt niet direct naar Odoo
                          gestuurd. De prijs wordt automatisch bijgewerkt in
                          Odoo op de gekozen ingangsdatum (
                          {new Date(effectiveDate).toLocaleDateString("nl-NL")}
                          ).
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {effectiveDate && isEffectiveDateImmediate() && (
                  <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                    <div className="flex items-start gap-2">
                      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-500">
                        <span className="text-xs font-bold text-white">✓</span>
                      </div>
                      <div className="text-sm">
                        <p className="mb-1 font-medium text-green-800">
                          Directe prijswijziging
                        </p>
                        <p className="text-green-700">
                          Deze prijswijziging wordt direct naar Odoo gestuurd
                          omdat de ingangsdatum vandaag of eerder is.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <Textarea
                  label="Reden voor prijswijziging"
                  value={priceChangeReason}
                  onChange={(e) => setPriceChangeReason(e.target.value)}
                  placeholder="Geef een reden op voor deze prijswijziging..."
                  minRows={3}
                  isRequired
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onClick={onClose}>
                Annuleren
              </Button>
              <Button color="primary" onClick={handleSave} isLoading={saving}>
                Prijswijziging opslaan
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

interface PriceHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: ContractProduct | null;
  onSuccess: () => void;
  onProductUpdate?: (updatedProduct: ContractProduct) => void; // New callback for instant updates
}

const PriceHistoryModal: React.FC<PriceHistoryModalProps> = ({
  isOpen,
  onClose,
  product,
  onSuccess,
  onProductUpdate,
}) => {
  const { session } = useSession();
  const [deleting, setDeleting] = useState(false);
  const [localProduct, setLocalProduct] = useState<ContractProduct | null>(
    null,
  );
  const [isPriceChangeModalOpen, setIsPriceChangeModalOpen] = useState(false);

  // Update local product when prop changes
  useEffect(() => {
    setLocalProduct(product);
  }, [product]);

  const formatDate = (dateString: string | Date): string => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("nl-NL");
  };

  const getPriceTypeLabel = (changeMetaData: string): string => {
    const labels: Record<string, string> = {
      INIT: "Initieel",
      ACTUAL_AT_CREATION: "Bij aanmaak",
      INDEXATION: "Indexatie",
      RAISE_RENT: "Verhaging",
      DISCOUNT: "Korting",
    };
    return labels[changeMetaData] || changeMetaData;
  };

  const getPriceTypeColor = (changeMetaData: string): string => {
    const colors: Record<string, string> = {
      INIT: "bg-blue-100 text-blue-800",
      ACTUAL_AT_CREATION: "bg-gray-100 text-gray-800",
      INDEXATION: "bg-green-100 text-green-800",
      RAISE_RENT: "bg-orange-100 text-orange-800",
      DISCOUNT: "bg-purple-100 text-purple-800",
    };
    return colors[changeMetaData] || "bg-gray-100 text-gray-800";
  };

  const canDeletePrice = (price: any): boolean => {
    if (!price.effectiveDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const effectiveDate = new Date(price.effectiveDate);
    effectiveDate.setHours(0, 0, 0, 0);
    // Can only delete future prices that haven't been pushed to accounting yet
    return effectiveDate > today && price.mustBePushedToAccounting === true;
  };

  const getAccountingStatus = (
    price: any,
    isCurrentPrice: boolean,
    isFuturePrice: boolean,
  ) => {
    if (!price.effectiveDate)
      return {
        type: "unknown",
        label: "Onbekend",
        icon: null,
        color: "bg-gray-100 text-gray-800",
      };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const effectiveDate = new Date(price.effectiveDate);
    effectiveDate.setHours(0, 0, 0, 0);

    const isEffective = effectiveDate <= today;
    const isPushedToAccounting = price.mustBePushedToAccounting === false;

    if (isFuturePrice) {
      return {
        type: "future",
        label: "Toekomstig",
        icon: null,
        color: "bg-amber-100 text-amber-800",
      };
    } else if (!isCurrentPrice && !isFuturePrice) {
      // Historical price - determine if it was pushed to accounting when it was effective
      if (isPushedToAccounting) {
        return {
          type: "historical_pushed",
          label: "Historisch",
          icon: <CheckCircle size={14} className="text-status-good" />,
          color: "bg-gray-100 text-gray-800",
          tooltip: "Was succesvol naar de boekhouding gepusht",
        };
      } else {
        return {
          type: "historical_not_pushed",
          label: "Historisch",
          icon: <Clock size={14} className="text-amber-600" />,
          color: "bg-gray-100 text-gray-800",
          tooltip: "Was niet naar de boekhouding gepusht",
        };
      }
    } else if (isCurrentPrice && isEffective) {
      // Current effective price
      if (isPushedToAccounting) {
        return {
          type: "current_pushed",
          label: "Huidig",
          icon: <CheckCircle size={14} className="text-status-good" />,
          color: "bg-green-100 text-green-800",
          tooltip: "Werd succesvol naar de boekhouding gepusht",
        };
      } else {
        return {
          type: "current_pending",
          label: "Huidig",
          icon: <Clock size={14} className="text-amber-600" />,
          color: "bg-green-100 text-green-800",
          tooltip: "Wordt nog naar de boekhouding gepusht",
        };
      }
    }

    // Fallback
    return {
      type: "unknown",
      label: "Onbekend",
      icon: null,
      color: "bg-gray-100 text-gray-800",
    };
  };

  const deletePrice = async (priceId: number) => {
    if (!confirm("Ben je zeker dat je deze prijswijziging wilt verwijderen?"))
      return;

    setDeleting(true);
    try {
      await fetchWithAuth(
        `${API_URL}/rental/prices/${priceId}`,
        { method: "DELETE" },
        session,
      );

      // Update local state immediately
      if (localProduct && localProduct.price) {
        const updatedProduct = {
          ...localProduct,
          price: localProduct.price.filter((p) => p.id !== priceId),
        };
        setLocalProduct(updatedProduct);

        // Also call the callback to update parent components
        if (onProductUpdate) {
          onProductUpdate(updatedProduct);
        }
      }

      toast.success("Prijswijziging succesvol verwijderd");
      onSuccess(); // Still call parent callback for any other updates
    } catch (error) {
      console.error("Error deleting price:", error);
      toast.error("Fout bij verwijderen van prijswijziging");
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenPriceChangeModal = () => {
    setIsPriceChangeModalOpen(true);
  };

  const handleClosePriceChangeModal = () => {
    setIsPriceChangeModalOpen(false);
  };

  const handlePriceChangeSuccess = () => {
    // The onProductUpdate callback will handle updating the local state
    // Also call the parent success callback
    onSuccess();
  };

  if (!localProduct) return null;

  // Reusable sorting function
  /**
   * Volgorde van de prijshistoriek: laatste ingangsdatum bovenaan, en binnen
   * dezelfde ingangsdatum de laatst geregistreerde wijziging bovenaan.
   *
   * De vorige vergelijking viel bij gelijke datums terug op de soort prijswijziging
   * en daarna op "hoogste prijs eerst". Twee wijzigingen op dezelfde dag kwamen
   * daardoor in de verkeerde volgorde te staan — en de oudste kreeg "Huidig".
   */
  const sortPricesWithPriority = (prices: any[]) => sortPriceHistory(prices);

  const sortedPrices = sortPricesWithPriority([...(localProduct.price || [])]);

  // Zelfde regel als de backend: de laatst geregistreerde onder de prijzen waarvan
  // de ingangsdatum bereikt is.
  const currentEffectivePrice = findActivePrice(localProduct.price || []);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onOpenChange={onClose}
        size="4xl"
        scrollBehavior="inside"
        className="text-neutral-primary font-normal"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ClockCounterClockwise
                      weight="duotone"
                      size={24}
                      className="text-moss-primary"
                    />
                    <h3 className="text-lg font-medium">Prijshistorie</h3>
                  </div>
                  <Button
                    color="primary"
                    variant="solid"
                    startContent={<Plus size={16} />}
                    onClick={handleOpenPriceChangeModal}
                    size="sm"
                    className="font-medium"
                  >
                    Nieuwe prijs
                  </Button>
                </div>
                <p className="text-neutral-secondary text-sm">
                  Product: {localProduct.name}
                </p>
              </ModalHeader>
              <ModalBody>
                {sortedPrices.length === 0 ? (
                  <p className="text-neutral-secondary py-8 text-center">
                    Geen prijshistorie gevonden voor dit product
                  </p>
                ) : (
                  <Table
                    aria-label="Prijshistorie"
                    classNames={{
                      wrapper: "shadow-none rounded-none p-0",
                      thead: "h-fit border-b-2 border-neutral-primary/40",
                      th: "rounded-none! bg-white py-2 h-fit text-sm text-neutral-primary font-medium",
                      tbody: "border-b divide-y divide-neutral-primary/15",
                      tr: "divide-x divide-neutral-primary/15",
                      td: "py-2.5",
                    }}
                  >
                    <TableHeader>
                      <TableColumn>Prijs</TableColumn>
                      <TableColumn>Type</TableColumn>
                      <TableColumn>Ingangsdatum</TableColumn>
                      <TableColumn>Registratiedatum</TableColumn>
                      <TableColumn>Reden</TableColumn>
                      <TableColumn>Status</TableColumn>
                      <TableColumn>Acties</TableColumn>
                    </TableHeader>
                    <TableBody>
                      {sortedPrices.map((price) => {
                        // Determine if this is a future price
                        const isFuturePrice = canDeletePrice(price);

                        // Use pre-calculated current effective price
                        const isCurrentPrice =
                          !!currentEffectivePrice &&
                          currentEffectivePrice.id === price.id;

                        return (
                          <TableRow key={price.id}>
                            <TableCell>
                              <span
                                className={`font-medium ${isCurrentPrice ? "text-moss-primary" : ""}`}
                              >
                                {applyMask(price.amount, "valuta_specific")}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="sm"
                                className={`${getPriceTypeColor(price.changeMetaData)}`}
                              >
                                {getPriceTypeLabel(price.changeMetaData)}
                              </Chip>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Calendar
                                  size={14}
                                  className="text-neutral-secondary"
                                />
                                <span>{formatDate(price.effectiveDate)}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-neutral-secondary text-sm">
                                {/* Met tijd zodra die bekend is, zodat twee
                                    wijzigingen op dezelfde dag te onderscheiden zijn. */}
                                {formatRegistrationMoment(price)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm">
                                {(price as any).priceChangeReason || "-"}
                              </span>
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const accountingStatus = getAccountingStatus(
                                  price,
                                  isCurrentPrice,
                                  isFuturePrice,
                                );

                                return (
                                  <div className="flex items-center gap-2">
                                    <Chip
                                      size="sm"
                                      className={accountingStatus.color}
                                    >
                                      {accountingStatus.label}
                                    </Chip>
                                    {accountingStatus.icon &&
                                    accountingStatus.tooltip ? (
                                      <Tooltip
                                        content={accountingStatus.tooltip}
                                      >
                                        <span className="cursor-help">
                                          {accountingStatus.icon}
                                        </span>
                                      </Tooltip>
                                    ) : accountingStatus.icon ? (
                                      accountingStatus.icon
                                    ) : null}
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              {isFuturePrice ? (
                                <Tooltip content="Verwijderen (alleen mogelijk voor toekomstige prijzen die nog niet naar boekhouding zijn gepusht)">
                                  <Button
                                    isIconOnly
                                    size="sm"
                                    variant="light"
                                    color="danger"
                                    onPress={() => deletePrice(price.id)}
                                    isLoading={deleting}
                                  >
                                    <Trash size={16} />
                                  </Button>
                                </Tooltip>
                              ) : (
                                <Tooltip content="Kan niet worden verwijderd (reeds effectief of naar boekhouding gepusht)">
                                  <span className="text-neutral-secondary cursor-not-allowed text-sm">
                                    -
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </ModalBody>
              <ModalFooter>
                <ButtonNew
                  variant="secondaryNeutral"
                  onClick={onClose}
                  label="Sluiten"
                />
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <PriceChangeModal
        isOpen={isPriceChangeModalOpen}
        onClose={handleClosePriceChangeModal}
        product={localProduct}
        onSuccess={handlePriceChangeSuccess}
        onProductUpdate={(updatedProduct) => {
          // Update the local state immediately
          setLocalProduct(updatedProduct);
          // Also call the parent callback
          if (onProductUpdate) {
            onProductUpdate(updatedProduct);
          }
        }}
      />
    </>
  );
};

interface ModalEditContractProductsProps {
  isOpen: boolean;
  onClose: () => void;
  contract: RentalContract | null;
  company: any; // Add company prop like modalAddContract
  onSuccess?: () => void;
}

const ModalEditContractProducts: React.FC<ModalEditContractProductsProps> = ({
  isOpen,
  onClose,
  contract,
  company,
  onSuccess,
}) => {
  const { session } = useSession();
  const [saving, setSaving] = useState(false);

  const getCurrentBrusselsDate = (): string => {
    // Create a date in Brussels timezone
    const now = new Date();
    const brusselsDate = new Date(
      now.toLocaleString("en-US", { timeZone: "Europe/Brussels" }),
    );
    const year = brusselsDate.getFullYear();
    const month = String(brusselsDate.getMonth() + 1).padStart(2, "0");
    const day = String(brusselsDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [products, setProducts] = useState<EditableProduct[]>([]);
  const [productTemplates, setProductTemplates] = useState<ProductTemplate[]>(
    [],
  );
  const [buildingAttributes, setBuildingAttributes] = useState<
    BuildingAttribute[]
  >([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isPriceChangeModalOpen, setIsPriceChangeModalOpen] = useState(false);
  const [selectedProductForPriceChange, setSelectedProductForPriceChange] =
    useState<ContractProduct | null>(null);
  const [isPriceHistoryModalOpen, setIsPriceHistoryModalOpen] = useState(false);
  const [selectedProductForPriceHistory, setSelectedProductForPriceHistory] =
    useState<ContractProduct | null>(null);
  const [isEditBasePriceModalOpen, setIsEditBasePriceModalOpen] =
    useState(false);
  const [selectedProductForBasePrice, setSelectedProductForBasePrice] =
    useState<ContractProduct | null>(null);
  const [isZeroConfirmOpen, setIsZeroConfirmOpen] = useState(false);
  // Templates load asynchronously; distinguish "still loading" from "none for this company"
  const [templatesLoading, setTemplatesLoading] = useState(false);
  // Only complain about empty prices once the user has actually tried to add
  const [addAttempted, setAddAttempted] = useState(false);
  // A building can carry dozens of attributes (bergingen + garageboxen); make them findable
  const [attributeSearch, setAttributeSearch] = useState("");
  const [newProduct, setNewProduct] = useState({
    name: "",
    basePrice: "",
    currentPrice: "",
    effectiveDate: "", // Will be set in useEffect
    templateId: null as number | null,
    buildingAttributeId: null as number | null,
  });

  // Set current Brussels date when modal opens
  useEffect(() => {
    if (isOpen) {
      setNewProduct((prev) => ({
        ...prev,
        effectiveDate: getCurrentBrusselsDate(),
      }));
    }
  }, [isOpen]);

  const fetchProductTemplates = useCallback(async () => {
    if (!company?.id) {
      console.log("No company ID available for fetching product templates");
      return;
    }

    try {
      setTemplatesLoading(true);
      console.log("Fetching product templates for company:", company.id);
      const response = await fetchWithAuth(
        `${API_URL}/companies/${company.id}/product-templates`,
        { method: "GET" },
        session,
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product templates: ${response.status}`,
        );
      }

      const data = await response.json();
      console.log("Product templates fetched:", data);
      setProductTemplates(data);
    } catch (error) {
      console.error("Error fetching product templates:", error);
      setProductTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, [company?.id, session]);

  const fetchBuildingAttributes = useCallback(async () => {
    if (!contract?.unit?.id) {
      setBuildingAttributes([]);
      return;
    }

    try {
      console.log("Fetching building attributes for unit:", contract.unit.id);
      const response = await fetchWithAuth(
        `${API_URL}/building-attributes/unit/${contract.unit.id}/available`,
        { method: "GET" },
        session,
      );

      if (response.ok) {
        const data = await response.json();
        console.log("Building attributes fetched:", data);
        setBuildingAttributes(data);
      } else {
        setBuildingAttributes([]);
      }
    } catch (error) {
      console.error("Error fetching building attributes:", error);
      setBuildingAttributes([]);
    }
  }, [contract?.unit?.id, session]);

  const loadContractProducts = useCallback(() => {
    if (!contract?.products) {
      setProducts([]);
      return;
    }

    // Use the products already loaded in the contract
    setProducts(contract.products.map((product) => ({ ...product })));
  }, [contract]);

  const fetchUpdatedContractData = useCallback(async () => {
    if (!contract?.id) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/contract/${contract.id}`,
        { method: "GET" },
        session,
      );
      const updatedContract = await response.json();

      // Update the products with the fresh data
      if (updatedContract.products) {
        setProducts(
          updatedContract.products.map((product: ContractProduct) => ({
            ...product,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching updated contract data:", error);
    }
  }, [contract?.id, session]);

  useEffect(() => {
    if (isOpen && contract && company) {
      console.log("Modal opened, contract data:", {
        contractId: contract.id,
        unitId: contract.unit?.id,
        buildingId: contract.unit?.building?.id,
        companyId: company?.id,
        companyName: company?.name,
      });

      loadContractProducts();
      fetchProductTemplates();
      fetchBuildingAttributes();
    }
  }, [
    isOpen,
    contract?.id,
    company?.id,
    contract?.unit?.id,
    loadContractProducts,
    fetchProductTemplates,
    fetchBuildingAttributes,
  ]);

  const parseDecimalInput = (value: string): number => {
    if (!value || value.trim() === "") return 0;

    const normalizedValue = value.replace(",", ".");
    const parsed = parseFloat(normalizedValue);

    return isNaN(parsed) ? 0 : parsed;
  };

  // Get base price (oldest effective price - initial price)
  const getBasePrice = (product: ContractProduct): number =>
    findBasePrice(product.price)?.amount ?? 0;

  // Get current effective price (most recent effective price, not in the future)
  /**
   * De prijs die vandaag geldt. Gebruikt de gedeelde regel — de laatst
   * geregistreerde van de prijzen waarvan de ingangsdatum bereikt is — zodat deze
   * modal hetzelfde bedrag toont als het Verhuur-overzicht, de indexatiemail en
   * Odoo. De vorige versie viel bij een gelijke ingangs- en registratiedatum terug
   * op "hoogste prijs wint", waardoor een latere correctie werd genegeerd.
   */
  const getCurrentPrice = (product: ContractProduct): number =>
    findActivePrice(product.price)?.amount ?? 0;

  // Get future prices
  const getFuturePrices = (product: ContractProduct) => {
    if (!product.price || product.price.length === 0) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const futurePrices = product.price.filter((price) => {
      if (!price.effectiveDate) return false;
      const effectiveDate = new Date(price.effectiveDate);
      effectiveDate.setHours(0, 0, 0, 0);
      return effectiveDate > today;
    });

    return futurePrices.sort((a, b) => {
      if (a.effectiveDate && b.effectiveDate) {
        return (
          new Date(a.effectiveDate).getTime() -
          new Date(b.effectiveDate).getTime()
        );
      }
      return a.id - b.id;
    });
  };

  const deleteProduct = async (productId: number) => {
    if (
      !confirm(
        `Ben je zeker dat je het product met naam ${products.find((p) => p.id === productId)?.name} wilt verwijderen?`,
      )
    )
      return;
    console.log("Deleting product with id " + productId);
    console.log(products.find((p) => p.id === productId)?.name);
    console.log("Contract id: " + contract?.id);

    setSaving(true);
    try {
      await fetchWithAuth(
        `${API_URL}/rental/contracts/${contract!.id}/products/${productId}`,
        { method: "DELETE" },
        session,
      );

      toast.success("Product succesvol verwijderd");
      onSuccess?.();
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Fout bij verwijderen van product");
    } finally {
      setSaving(false);
    }
  };

  /**
   * @param skipZeroPriceConfirm set by the "Toch opslaan" button of the zero-price warning.
   * Without it the confirmation re-entered this function, hit the same zero check and
   * re-opened itself — so a gebouwattribuut at price 0 could never be added.
   */
  // Filter on name and on soort ("berging", "garagebox"), sorted so the numbering reads
  // naturally: Berging 69-2 before Berging 69-10.
  const visibleBuildingAttributes = useMemo(() => {
    const term = attributeSearch.trim().toLowerCase();
    return (buildingAttributes || [])
      .filter((attribute: any) =>
        term
          ? attribute.name?.toLowerCase().includes(term) ||
            attribute.template?.category?.toLowerCase().includes(term)
          : true,
      )
      .sort((a: any, b: any) => {
        const byCategory = (a.template?.category || "").localeCompare(
          b.template?.category || "",
          "nl",
          { sensitivity: "base" },
        );
        if (byCategory !== 0) return byCategory;
        return (a.name || "").localeCompare(b.name || "", "nl", {
          numeric: true,
          sensitivity: "base",
        });
      });
  }, [buildingAttributes, attributeSearch]);

  const addProduct = async (skipZeroPriceConfirm = false) => {
    setAddAttempted(true);

    if (!newProduct.templateId) {
      toast.error(
        productTemplates.length === 0
          ? "Er zijn geen producten beschikbaar voor deze vennootschap"
          : "Kies eerst een product",
      );
      return;
    }

    if (!newProduct.name.trim()) {
      toast.error("Productnaam is verplicht");
      return;
    }

    // Validate that either basePrice or currentPrice is provided (unless a building attribute is selected)
    const basePrice = parseDecimalInput(newProduct.basePrice);
    const currentPrice = parseDecimalInput(newProduct.currentPrice);
    const isBuildingAttributeSelected = !!newProduct.buildingAttributeId;

    if (!isBuildingAttributeSelected && basePrice <= 0 && currentPrice <= 0) {
      toast.error(
        "Ten minste één prijs moet worden opgegeven (tenzij een gebouwattribuut is geselecteerd)",
      );
      return;
    }

    // If any of the prices is 0, ask for confirmation first
    if (!skipZeroPriceConfirm && (basePrice <= 0 || currentPrice <= 0)) {
      setIsZeroConfirmOpen(true);
      return;
    }

    setSaving(true);
    try {
      const selectedTemplate = productTemplates.find(
        (t) => t.id === newProduct.templateId,
      );

      const productData = {
        name: newProduct.name,
        basePrice: basePrice,
        indexedPrice: currentPrice > 0 ? currentPrice : basePrice, // Use current price if provided, otherwise base price
        effectiveDate: newProduct.effectiveDate,
        templateId: newProduct.templateId,
        buildingAttributeId: newProduct.buildingAttributeId,
        odooProductVariantId: selectedTemplate?.odooProductVariantId || null,
        odooTemplateName: selectedTemplate?.odooTemplateName || null,
        ledger: selectedTemplate?.ledger || null,
        indexable: selectedTemplate?.indexable || false,
      };

      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/${contract!.id}/products`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(productData),
        },
        session,
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add product");
      }

      // Soft update: update productenlijst op basis van de respons
      try {
        const data = await response.json();

        // The backend reports whether the line reached Odoo; saying "toegevoegd" while the
        // boekhouding never saw it is how silent drift starts.
        const sync = data?.odooSync;
        if (sync?.status === "failed") {
          toast.warning(
            `Product toegevoegd, maar niet naar Odoo gesynchroniseerd: ${sync.reason || "onbekende reden"}`,
            { autoClose: 8000 },
          );
        } else if (sync?.status === "skipped") {
          toast.info(`Product toegevoegd (niet naar Odoo: ${sync.reason})`);
        } else {
          toast.success("Product toegevoegd en gesynchroniseerd met Odoo");
        }

        if (data && Array.isArray(data.products)) {
          // Backend retourneert het volledige contract met products
          setProducts(data.products.map((p: any) => ({ ...p })));
        } else if (data && data.id) {
          // Backend retourneert het aangemaakte product
          setProducts((prev) => {
            const alreadyExists = prev.some((p) => p.id === data.id);
            return alreadyExists ? prev : [...prev, { ...data }];
          });
        } else {
          // Onbekende payload → fallback naar refetch
          await fetchUpdatedContractData();
        }
      } catch (_) {
        // Geen/ongeldige JSON → fallback naar refetch
        await fetchUpdatedContractData();
      }

      // Remove selected building attribute from available list (if any)
      if (newProduct.buildingAttributeId) {
        setBuildingAttributes((prev) =>
          prev.filter((a) => a.id !== newProduct.buildingAttributeId),
        );
      }

      // Reset form
      setAddAttempted(false);
      setNewProduct({
        name: "",
        basePrice: "",
        currentPrice: "",
        effectiveDate: getCurrentBrusselsDate(), // Always current Brussels date
        templateId: null,
        buildingAttributeId: null,
      });
      setShowAddForm(false);

      // Refresh data
      onSuccess?.();
    } catch (error) {
      console.error("Error adding product:", error);
      toast.error((error as Error).message || "Fout bij toevoegen van product");
    } finally {
      setSaving(false);
    }
  };

  const handleAddBuildingAttribute = (attribute: BuildingAttribute) => {
    // Check if this attribute is already added to the contract
    const alreadyAdded = products.some(
      (product) => product.buildingAttribute?.id === attribute.id,
    );

    if (alreadyAdded) {
      toast.error(`${attribute.name} is al toegevoegd aan het contract`);
      return;
    }

    // Find the matching product template for this company
    const matchingTemplate = attribute.template.productTemplates?.find(
      (pt) => pt.company.id === company?.id,
    );

    if (!matchingTemplate) {
      toast.error(
        `Geen product template gevonden voor ${attribute.template.category} voor dit bedrijf`,
      );
      return;
    }

    // Find the full template from productTemplates to get all properties
    const fullTemplate = productTemplates.find(
      (t) => t.id === matchingTemplate.id,
    );

    if (!fullTemplate) {
      toast.error(`Product template niet gevonden in beschikbare templates`);
      return;
    }

    // Set the form with the building attribute data
    setNewProduct({
      name: fullTemplate.name,
      basePrice: "0",
      currentPrice: "0",
      effectiveDate: getCurrentBrusselsDate(), // Use current Brussels date
      templateId: fullTemplate.id,
      buildingAttributeId: attribute.id,
    });

    // Show the add form if not already visible
    setShowAddForm(true);

    toast.success(
      `${attribute.name} geselecteerd - pas de prijzen aan en klik op toevoegen`,
    );
  };

  const handleOpenPriceChangeModal = (product: ContractProduct) => {
    setSelectedProductForPriceChange(product);
    setIsPriceChangeModalOpen(true);
  };

  const handleClosePriceChangeModal = () => {
    setSelectedProductForPriceChange(null);
    setIsPriceChangeModalOpen(false);
  };

  const handleOpenPriceHistoryModal = (product: ContractProduct) => {
    setSelectedProductForPriceHistory(product);
    setIsPriceHistoryModalOpen(true);
  };

  const handleClosePriceHistoryModal = () => {
    setSelectedProductForPriceHistory(null);
    setIsPriceHistoryModalOpen(false);
  };

  const handleOpenEditBasePriceModal = (product: ContractProduct) => {
    setSelectedProductForBasePrice(product);
    setIsEditBasePriceModalOpen(true);
  };

  const handleCloseEditBasePriceModal = () => {
    setSelectedProductForBasePrice(null);
    setIsEditBasePriceModalOpen(false);
  };

  const handleEditBasePriceSuccess = () => {
    fetchUpdatedContractData();
    if (onSuccess) {
      onSuccess();
    }
  };

  const updateLocalProductData = (updatedProduct: ContractProduct) => {
    // Update the products array in the main modal
    setProducts((prevProducts) =>
      prevProducts.map((product) =>
        product.id === updatedProduct.id ? updatedProduct : product,
      ),
    );
  };

  const handlePriceChangeSuccess = () => {
    // Refresh the contract data in this modal
    fetchUpdatedContractData();

    // Also refresh the parent component
    if (onSuccess) {
      onSuccess();
    }
  };

  const handlePriceHistorySuccess = () => {
    // Refresh the contract data in this modal
    fetchUpdatedContractData();

    // Also refresh the parent component
    if (onSuccess) {
      onSuccess();
    }
  };

  const handleClose = () => {
    setProducts([]);
    setShowAddForm(false);
    setAddAttempted(false);
    setNewProduct({
      name: "",
      basePrice: "",
      currentPrice: "",
      effectiveDate: "",
      templateId: null,
      buildingAttributeId: null,
    });
    onClose();
  };

  return (
    <>
      {/* Zero price confirmation modal */}
      <Modal
        isOpen={isZeroConfirmOpen}
        onOpenChange={() => setIsZeroConfirmOpen(false)}
        size="md"
        className="text-neutral-primary font-normal"
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h3 className="text-lg font-medium">
                  Bevestig opslaan met prijs 0
                </h3>
                <p className="text-md text-red-600">
                  LEES DIT EERST AANDACHTIG DOOR!
                </p>
                <p className="text-neutral-secondary text-sm">
                  Je moet normaal altijd een basishuurprijs EN geïndexeerde
                  prijs ingeven die &gt; 0.
                </p>
                <p className="text-sm text-red-600">
                  De eerste keer dat een prijs wordt vastgelegd is de
                  geïndexeerde prijs ALTIJD gelijk aan de basishuurprijs. Het is
                  pas door de jaren heen dat de geïndexeerde prijs steeds hoger
                  wordt dan de basishuurprijs. (door de wettelijke indexatie)
                </p>
              </ModalHeader>
              <ModalBody>
                <div className="space-y-2">
                  <p className="text-sm">
                    Ben je zeker dat je dit wil opslaan met volgende waarden?
                  </p>
                  <ul className="list-disc pl-5 text-sm">
                    <li
                      className={
                        parseDecimalInput(newProduct.basePrice) <= 0
                          ? "font-medium text-red-600"
                          : ""
                      }
                    >
                      Basis huurprijs: € {newProduct.basePrice || "0,00"}
                    </li>
                    <li
                      className={
                        parseDecimalInput(newProduct.currentPrice) <= 0
                          ? "font-medium text-red-600"
                          : ""
                      }
                    >
                      Geïndexeerde prijs: € {newProduct.currentPrice || "0,00"}
                    </li>
                  </ul>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  variant="light"
                  onClick={() => setIsZeroConfirmOpen(false)}
                >
                  Annuleren
                </Button>
                <Button
                  color="primary"
                  onClick={async () => {
                    setIsZeroConfirmOpen(false);
                    await addProduct(true);
                  }}
                >
                  Toch opslaan
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isOpen}
        onOpenChange={handleClose}
        size="5xl"
        scrollBehavior="inside"
        className="text-neutral-primary font-normal"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <Pencil
                    weight="duotone"
                    size={24}
                    className="text-moss-primary"
                  />
                  <h3 className="text-lg font-medium">
                    Contractproducten bewerken
                  </h3>
                </div>
                {contract && (
                  <p className="text-neutral-secondary text-sm">
                    Contract voor unit: {contract.unit?.id}{" "}
                    {contract.unit?.referencePlus
                      ? `(${contract.unit.referencePlus})`
                      : ""}
                  </p>
                )}
              </ModalHeader>
              <ModalBody>
                <div className="space-y-6">
                  {/* Existing Products Table */}
                  <div>
                    <div className="mb-4 flex items-center justify-between">
                      <h4 className="text-lg font-medium">
                        Bestaande producten
                      </h4>
                      <ButtonNew
                        onClick={() => setShowAddForm(!showAddForm)}
                        icon={<Plus />}
                        label="Product toevoegen"
                        variant="primaryMoss"
                      />
                    </div>

                    {products.length === 0 ? (
                      <p className="text-neutral-secondary py-8 text-center">
                        Geen producten gevonden voor dit contract
                      </p>
                    ) : (
                      <Table
                        aria-label="Contract producten"
                        classNames={{
                          wrapper: "shadow-none rounded-none p-0",
                          thead: "h-fit border-b-2 border-neutral-primary/40",
                          th: "rounded-none! bg-white py-2 h-fit text-sm text-neutral-primary font-medium",
                          tbody: "border-b divide-y divide-neutral-primary/15",
                          tr: "divide-x divide-neutral-primary/15",
                          td: "py-2.5",
                        }}
                      >
                        <TableHeader>
                          <TableColumn>Product</TableColumn>
                          <TableColumn>Template</TableColumn>
                          <TableColumn>Basis huurprijs</TableColumn>
                          <TableColumn>Huidige huurprijs</TableColumn>
                          <TableColumn>Toekomstige prijzen</TableColumn>
                          <TableColumn>Gebouw attribuut</TableColumn>
                          <TableColumn>Acties</TableColumn>
                        </TableHeader>
                        <TableBody>
                          {products.map((product) => (
                            <TableRow key={product.id}>
                              <TableCell>
                                <span className="font-medium">
                                  {product.name}
                                </span>
                              </TableCell>
                              <TableCell>
                                {product.template ? (
                                  <div className="flex flex-col">
                                    <span className="font-medium">
                                      {product.template.name}
                                    </span>
                                    <span className="text-neutral-secondary text-xs">
                                      ID: {product.template.id} | Bedrijf:{" "}
                                      {product.template.company?.name ||
                                        "Onbekend"}
                                    </span>
                                  </div>
                                ) : (
                                  <span>-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">
                                    {applyMask(
                                      getBasePrice(product),
                                      "valuta_specific",
                                    )}
                                  </span>
                                  <Tooltip content="Basisprijs bewerken">
                                    <Button
                                      isIconOnly
                                      size="sm"
                                      variant="light"
                                      className="text-blue-600 hover:bg-blue-50"
                                      onPress={() =>
                                        handleOpenEditBasePriceModal(product)
                                      }
                                    >
                                      <Pencil size={14} />
                                    </Button>
                                  </Tooltip>
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="font-medium">
                                  {applyMask(
                                    getCurrentPrice(product),
                                    "valuta_specific",
                                  )}
                                </span>
                              </TableCell>
                              <TableCell>
                                {getFuturePrices(product).length > 0 ? (
                                  <div className="space-y-1">
                                    {getFuturePrices(product).map(
                                      (futurePrice, index) => (
                                        <div
                                          key={index}
                                          className="flex items-center gap-2"
                                        >
                                          <Calendar
                                            size={14}
                                            className="text-brick-primary"
                                          />
                                          <span className="text-sm">
                                            {applyMask(
                                              futurePrice.amount,
                                              "valuta_specific",
                                            )}
                                          </span>
                                          <span className="text-neutral-secondary text-xs">
                                            (
                                            {new Date(
                                              futurePrice.effectiveDate!,
                                            ).toLocaleDateString("nl-NL")}
                                            )
                                          </span>
                                        </div>
                                      ),
                                    )}
                                    <div className="mt-2">
                                      <Tooltip content="Bekijk alle prijzen">
                                        <Button
                                          size="sm"
                                          variant="light"
                                          className="text-moss-primary hover:bg-moss-primary hover:text-white"
                                          onPress={() =>
                                            handleOpenPriceHistoryModal(product)
                                          }
                                          startContent={
                                            <ClockCounterClockwise size={14} />
                                          }
                                        >
                                          Prijshistorie
                                        </Button>
                                      </Tooltip>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <span className="text-neutral-secondary">
                                      -
                                    </span>
                                    <Tooltip content="Bekijk alle prijzen">
                                      <Button
                                        size="sm"
                                        variant="light"
                                        className="text-moss-primary hover:bg-moss-primary hover:text-white"
                                        onPress={() =>
                                          handleOpenPriceHistoryModal(product)
                                        }
                                        startContent={
                                          <ClockCounterClockwise size={14} />
                                        }
                                      >
                                        Prijshistorie
                                      </Button>
                                    </Tooltip>
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                {product.buildingAttribute ? (
                                  <Chip
                                    size="sm"
                                    className="bg-moss-primary/10 text-moss-primary border-moss-primary/20 border"
                                  >
                                    {product.buildingAttribute.name}
                                  </Chip>
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Tooltip content="Prijs wijzigen">
                                    <Button
                                      isIconOnly
                                      size="sm"
                                      variant="light"
                                      className="text-brick-primary hover:bg-brick-primary hover:text-white"
                                      onPress={() =>
                                        handleOpenPriceChangeModal(product)
                                      }
                                    >
                                      <Pencil size={16} />
                                    </Button>
                                  </Tooltip>
                                  <Tooltip content="Verwijderen">
                                    <Button
                                      isIconOnly
                                      size="sm"
                                      variant="light"
                                      color="danger"
                                      onPress={() => deleteProduct(product.id)}
                                      isLoading={saving}
                                    >
                                      <Trash size={16} />
                                    </Button>
                                  </Tooltip>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>

                  {/* Add New Product Form */}
                  {showAddForm && (
                    <div className="border-t pt-6">
                      <h4 className="mb-4 text-lg font-medium">
                        Nieuw product toevoegen
                      </h4>

                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <Select
                            label="Product"
                            placeholder={
                              templatesLoading
                                ? "Producten laden…"
                                : productTemplates.length === 0
                                  ? "Geen producten beschikbaar"
                                  : "Kies een product"
                            }
                            isDisabled={
                              templatesLoading || productTemplates.length === 0
                            }
                            isInvalid={addAttempted && !newProduct.templateId}
                            description={
                              templatesLoading
                                ? "Producten van deze vennootschap worden geladen"
                                : productTemplates.length === 0
                                  ? company?.id
                                    ? `${company?.name || "Deze vennootschap"} heeft nog geen Yally producten. Voeg ze toe via Instellingen → Product varianten.`
                                    : "De vennootschap van dit contract is niet bekend, waardoor er geen producten geladen kunnen worden."
                                  : "De naam en grootboekrekening komen uit het product"
                            }
                            selectedKeys={
                              newProduct.templateId
                                ? [newProduct.templateId.toString()]
                                : []
                            }
                            onSelectionChange={(keys) => {
                              const selectedId = Array.from(keys)[0] as string;
                              const template = productTemplates.find(
                                (t) => t.id.toString() === selectedId,
                              );
                              setNewProduct({
                                ...newProduct,
                                templateId: selectedId
                                  ? parseInt(selectedId)
                                  : null,
                                name: template?.name || "", // Auto-fill name when template is selected
                              });
                            }}
                            isRequired
                          >
                            {productTemplates.map((template) => (
                              <SelectItem
                                key={template.id.toString()}
                                textValue={template.name}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {template.name}
                                  </span>
                                  <span className="text-neutral-secondary text-xs">
                                    Grootboek:{" "}
                                    {template.ledger || "Niet ingesteld"}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </Select>
                          <Input
                            label="Productnaam"
                            value={newProduct.name}
                            onChange={(e) =>
                              setNewProduct({
                                ...newProduct,
                                name: e.target.value,
                              })
                            }
                            placeholder={
                              newProduct.templateId
                                ? "Naam op de contractlijn"
                                : "Kies eerst een product"
                            }
                            isRequired
                            description={
                              newProduct.templateId
                                ? "Zo verschijnt de lijn op de factuur — aanpasbaar"
                                : undefined
                            }
                          />
                        </div>

                        {/* Show template details if selected */}
                        {newProduct.templateId && (
                          <div className="bg-moss-primary/5 border-moss-primary/20 rounded-lg border p-3">
                            <h6 className="text-moss-primary mb-2 font-medium">
                              Template details
                            </h6>
                            {(() => {
                              const selectedTemplate = productTemplates.find(
                                (t) => t.id === newProduct.templateId,
                              );
                              return selectedTemplate ? (
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="text-neutral-secondary">
                                      Naam:
                                    </span>
                                    <p className="font-medium">
                                      {selectedTemplate.name}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-neutral-secondary">
                                      Grootboekrekening:
                                    </span>
                                    <p className="font-medium">
                                      {selectedTemplate.ledger ||
                                        "Niet ingesteld"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-neutral-secondary">
                                      Indexeerbaar:
                                    </span>
                                    <p className="font-medium">
                                      {selectedTemplate.indexable
                                        ? "Ja"
                                        : "Nee"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="text-neutral-secondary">
                                      Odoo ID:
                                    </span>
                                    <p className="font-medium">
                                      {selectedTemplate.odooProductVariantId ||
                                        "Niet gekoppeld"}
                                    </p>
                                  </div>
                                </div>
                              ) : null;
                            })()}
                          </div>
                        )}

                        <div className="grid grid-cols-3 gap-4">
                          <Input
                            label="Basis huurprijs"
                            value={newProduct.basePrice}
                            onChange={(e) =>
                              setNewProduct({
                                ...newProduct,
                                basePrice: e.target.value,
                              })
                            }
                            placeholder="0,00"
                            startContent={
                              <span className="text-neutral-secondary">€</span>
                            }
                            isInvalid={
                              addAttempted &&
                              parseDecimalInput(newProduct.basePrice) <= 0
                            }
                            errorMessage={
                              addAttempted &&
                              parseDecimalInput(newProduct.basePrice) <= 0
                                ? "Vul een bedrag in"
                                : ""
                            }
                            description="Basishuurprijs bij contractstart"
                          />
                          <Input
                            label="Huidige huurprijs"
                            value={newProduct.currentPrice}
                            onChange={(e) =>
                              setNewProduct({
                                ...newProduct,
                                currentPrice: e.target.value,
                              })
                            }
                            placeholder="0,00"
                            startContent={
                              <span className="text-neutral-secondary">€</span>
                            }
                            isInvalid={
                              addAttempted &&
                              parseDecimalInput(newProduct.currentPrice) <= 0
                            }
                            errorMessage={
                              addAttempted &&
                              parseDecimalInput(newProduct.currentPrice) <= 0
                                ? "Vul een bedrag in"
                                : ""
                            }
                            description="Huidige effectieve prijs"
                          />
                          <div className="border-neutral-primary/15 bg-neutral-primary/3 flex flex-col justify-center rounded-medium border px-3 py-2">
                            <span className="text-neutral-secondary text-xs">
                              Ingangsdatum
                            </span>
                            <span className="text-neutral-primary text-sm font-medium">
                              Vandaag —{" "}
                              {newProduct.effectiveDate
                                ? new Date(
                                    newProduct.effectiveDate,
                                  ).toLocaleDateString("nl-BE")
                                : ""}
                            </span>
                            <span className="text-neutral-secondary mt-0.5 text-xs">
                              Het product gaat onmiddellijk in
                            </span>
                          </div>
                        </div>

                        {/* Huurprijs uitleg & hulpmiddel */}
                        <div className="border-neutral-primary/15 bg-neutral-primary/3 rounded-lg border p-4">
                          <h5 className="text-neutral-primary mb-2 text-sm font-medium">
                            Uitleg huurprijzen en indexatie
                          </h5>
                          <ul className="text-neutral-primary mb-3 list-disc pl-5 text-sm">
                            <li>
                              <span className="font-medium">
                                Basishuurprijs
                              </span>
                              : de huurprijs zoals die in het contract staat bij
                              aanvang.
                            </li>
                            <li>
                              <span className="font-medium">
                                Geïndexeerde huurprijs
                              </span>
                              : de actuele, meestal betaalde huurprijs na
                              wettelijke indexatie (op basis van de
                              gezondheidsindex).
                            </li>
                          </ul>
                          <div className="text-neutral-secondary text-xs">
                            Standaardformule (BE): Nieuwe huurprijs = (Huidige
                            index / Startindex) × Basishuurprijs. Gebruik bij
                            twijfel de officiële{" "}
                            <a
                              href="https://statbel.fgov.be/nl/themas/consumptieprijsindex/huurcalculator"
                              target="_blank"
                              rel="noreferrer"
                              className="text-moss-primary hover:text-moss-primary/90 font-medium underline"
                            >
                              huurcalculator van Statbel
                            </a>
                            .
                          </div>
                          <p className="text-neutral-secondary mt-2 text-xs">
                            De eerste keer dat een prijs wordt vastgelegd is de
                            geïndexeerde prijs{" "}
                            <span className="font-medium">altijd gelijk</span>{" "}
                            aan de basishuurprijs. Pas door de jaren heen wordt
                            de geïndexeerde prijs hoger dan de basishuurprijs
                            (door wettelijke indexatie).
                          </p>
                        </div>

                        {/* Building Attributes */}
                        {buildingAttributes.length > 0 && (
                          <div className="border-t pt-4">
                            <div className="mb-2 flex items-center justify-between gap-2">
                              <h5 className="text-neutral-primary font-medium">
                                Of selecteer een gebouwattribuut
                              </h5>
                              <span className="text-neutral-secondary-mid text-xs">
                                {visibleBuildingAttributes.length} van{" "}
                                {buildingAttributes.length}
                              </span>
                            </div>
                            {buildingAttributes.length > 8 && (
                              <Input
                                value={attributeSearch}
                                onChange={(e) =>
                                  setAttributeSearch(e.target.value)
                                }
                                placeholder="Zoek op naam of soort (bv. GB 12, berging)"
                                size="sm"
                                className="mb-2"
                                startContent={
                                  <MagnifyingGlass
                                    size={16}
                                    className="text-neutral-secondary-mid"
                                  />
                                }
                              />
                            )}

                            {/* Dark label on white instead of moss-on-white: with 37 of these
                                side by side, green text at 12-14px is simply hard to read. */}
                            <div className="border-neutral-primary/15 grid max-h-56 grid-cols-2 gap-1.5 overflow-y-auto rounded-lg border p-2 md:grid-cols-3 lg:grid-cols-4">
                              {visibleBuildingAttributes.length === 0 ? (
                                <p className="text-neutral-secondary-mid col-span-full p-2 text-sm">
                                  Geen attributen gevonden
                                </p>
                              ) : (
                                visibleBuildingAttributes.map((attribute) => {
                                  const isSelected =
                                    newProduct.buildingAttributeId ===
                                    attribute.id;
                                  return (
                                    <button
                                      key={attribute.id}
                                      type="button"
                                      onClick={() =>
                                        handleAddBuildingAttribute(attribute)
                                      }
                                      className={`flex min-w-0 flex-col items-start rounded-md border px-2.5 py-1.5 text-left transition-colors ${
                                        isSelected
                                          ? "bg-moss-primary border-moss-primary text-white"
                                          : "border-neutral-primary/20 hover:border-moss-primary hover:bg-moss-primary/5"
                                      }`}
                                    >
                                      <span
                                        className={`w-full truncate text-sm font-medium ${
                                          isSelected
                                            ? "text-white"
                                            : "text-neutral-primary"
                                        }`}
                                      >
                                        {attribute.name}
                                      </span>
                                      <span
                                        className={`w-full truncate text-xs ${
                                          isSelected
                                            ? "text-white/80"
                                            : "text-neutral-secondary-mid"
                                        }`}
                                      >
                                        {attribute.template.category}
                                      </span>
                                      <AttributeExistingContractBadge
                                        attribute={attribute}
                                        compact
                                        className="mt-1"
                                      />
                                    </button>
                                  );
                                })
                              )}
                            </div>
                            <p className="text-neutral-secondary-mid mt-2 text-xs">
                              Klik op een attribuut om automatisch de juiste
                              productgegevens in te vullen
                            </p>
                          </div>
                        )}

                        <div className="flex items-center justify-between border-t pt-4">
                          <div className="text-neutral-secondary text-sm">
                            {newProduct.templateId && (
                              <span>
                                Template geselecteerd:{" "}
                                {
                                  productTemplates.find(
                                    (t) => t.id === newProduct.templateId,
                                  )?.name
                                }
                              </span>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="light"
                              onClick={() => {
                                setShowAddForm(false);
                                setAddAttempted(false);
                                setNewProduct({
                                  name: "",
                                  basePrice: "",
                                  currentPrice: "",
                                  effectiveDate: "",
                                  templateId: null,
                                  buildingAttributeId: null,
                                });
                              }}
                            >
                              Annuleren
                            </Button>
                            <Button
                              color="primary"
                              onClick={() => addProduct()}
                              isLoading={saving}
                              isDisabled={
                                !newProduct.templateId ||
                                !newProduct.name.trim() ||
                                !newProduct.effectiveDate.trim()
                              }
                            >
                              Product toevoegen
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </ModalBody>
              <ModalFooter>
                <ButtonNew
                  variant="secondaryNeutral"
                  onClick={onClose}
                  label="Sluiten"
                />
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <PriceChangeModal
        isOpen={isPriceChangeModalOpen}
        onClose={handleClosePriceChangeModal}
        product={selectedProductForPriceChange}
        onSuccess={handlePriceChangeSuccess}
        onProductUpdate={(updatedProduct) => {
          // Update the product in the price history modal if it's the same product
          if (
            selectedProductForPriceHistory &&
            selectedProductForPriceHistory.id === updatedProduct.id
          ) {
            setSelectedProductForPriceHistory(updatedProduct);
          }
          // Update the main products list
          updateLocalProductData(updatedProduct);
        }}
      />

      <PriceHistoryModal
        isOpen={isPriceHistoryModalOpen}
        onClose={handleClosePriceHistoryModal}
        product={selectedProductForPriceHistory}
        onSuccess={handlePriceHistorySuccess}
        onProductUpdate={(updatedProduct) => {
          // Update the main products list
          updateLocalProductData(updatedProduct);
        }}
      />

      <EditBasePriceModal
        isOpen={isEditBasePriceModalOpen}
        onClose={handleCloseEditBasePriceModal}
        product={selectedProductForBasePrice}
        onSuccess={handleEditBasePriceSuccess}
        onProductUpdate={(updatedProduct) => {
          // Update the main products list
          updateLocalProductData(updatedProduct);
        }}
      />
    </>
  );
};

export default ModalEditContractProducts;
